import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { AnimatedButtonArrows } from "@/components/adg4/animated-button-arrows/src";
import { Adg4Button } from "@/components/adg4/button/src";
import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import TryIntent from "@/components/try-intent/src";
import { useLocalizedHref } from "@/libraries/web-framework";

const sectionContainer = css({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    margin: "0 auto",
    maxWidth: 1337,
    minHeight: 470,
    overflow: "hidden"
});

const contentContainer = css({
    position: "relative",
    display: "flex",
    flexDirection: "row",
    maxWidth: 1200,
    margin: "auto",
    paddingInlineStart: 64,
    paddingInlineEnd: 64,
    paddingBlockStart: 96,
    paddingBlockEnd: 96,
    gap: 32,

    [media.below.md]: {
        flexDirection: "column",
        gap: 60,
        paddingBlockEnd: "3.5rem",
        paddingBlockStart: "7.5rem"
    }
});

const backgroundBlue = css({
    position: "absolute",
    clipPath: "polygon(3% 11%, 100% 13%, 99% 90%, 2% 87%)",
    backgroundColor: "#1868DB",
    minWidth: 1337,
    width: "100%",
    height: "100%",
    insetBlockStart: 0,
    insetBlockEnd: 0,

    [media.below.md]: {
        minWidth: "100%",
        clipPath: "polygon(0 5%, 100% 10%, 100% 100%, 0 100%)"
    }
});

const backgroundYellow = css({
    position: "absolute",
    clipPath: "polygon(0 30.5%, 96% 5.5%, 96% 79%, 1.5% 95.5%)",
    backgroundColor: "#FFAB00",
    minWidth: 1337,
    width: "100%",
    height: "100%",
    insetBlockStart: 0,
    insetBlockEnd: 0,

    [media.below.md]: {
        minWidth: "100%",
        clipPath: "polygon(0 6%, 100% 0, 100% 50%, 0 50%)"
    }
});

const textSection = css({
    [media.below.md]: {
        textAlign: "center",
        maxWidth: 530
    }
});
const headingText = css({
    margin: 0,
    color: "#fff !important",
    [media.below.xs]: {
        marginBlockEnd: "1.5rem"
    }
});
const subText = css({
    marginBlockStart: ".75rem",
    marginBlockEnd: "1.5rem",
    maxWidth: 445,
    color: "#fff !important",
    [media.below.md]: {
        marginInline: "auto",
        maxWidth: 345
    }
});

const graphicSection = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    flex: "0 0 40%"
});

const replacesStyle = css({
    marginBlockStart: "2.5rem",
    opacity: 0.9,
    mixBlendMode: "screen",
    pointerEvents: "none"
});

const CtaSection = () => {
    const signupLink = useLocalizedHref("/try/cloud/signup?bundle=confluence");
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.CONFLUENCE,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });
    return (
        <UFOSegment name="confluence-pt-cfind-1234-ctaSection">
            <section css={sectionContainer}>
                <div css={backgroundYellow} />
                <div css={backgroundBlue} />
                <div css={contentContainer}>
                    <div css={textSection}>
                        <h2 css={headingText}>
                            <FormattedMessage
                                id="ConfluenceSoftwarePage.v4.ctaSection.header"
                                defaultMessage="Ditch your old docs, collaborate effortlessly with Confluence"
                            />
                        </h2>
                        <p css={subText}>
                            <FormattedMessage
                                id="ConfluenceSoftwarePage.v4.ctaSection.subtext"
                                defaultMessage="Confluence is your connected workspace, designed for collaboration and built to last"
                            />
                        </p>
                        <TryIntent
                            intentType={"button"}
                            containerId={"ctaSection"}
                            productKey={"confluence"}
                        >
                            <Adg4Button
                                href={signupLink}
                                variant="primary"
                                color="yellow"
                                size="large"
                                onClick={openCrossFlow}
                                dataEventProps={{
                                    "data-product-key": "confluence",
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "ctaSection",
                                    "data-label": "cta-get-confluence-free",
                                    "data-label-english": "Get Confluence free",
                                    "data-track-event":
                                        "confluence-pt.cta.get-confluence-free"
                                }}
                            >
                                <FormattedMessage
                                    id="ConfluenceSoftwarePage.v4.heroCTA"
                                    defaultMessage="Get Confluence free"
                                />
                            </Adg4Button>
                        </TryIntent>
                    </div>
                    <div css={graphicSection}>
                        <TryIntent
                            intentType={"button"}
                            containerId={"ctaSection"}
                            productKey={"confluence"}
                        >
                            <AnimatedButtonArrows
                                product="confluence"
                                href={signupLink}
                                onClick={openCrossFlow}
                                color="#fff"
                            />
                        </TryIntent>
                        <img
                            css={replacesStyle}
                            src="https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/confluencelp-replaces.webp"
                            alt=""
                            role="presentation"
                        />
                    </div>
                </div>
            </section>
        </UFOSegment>
    );
};

CtaSection.displayName = "CtaSection";

export default CtaSection;
